import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Check, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ConnectedLeague {
  league_id: string;
  league_name: string;
  season_year: number;
  is_active: boolean;
}

interface DiscoveredLeague {
  league_id: string;
  name: string;
  season: string;
  total_rosters: number | null;
  avatar: string | null;
  settings: any;
  scoring_settings: any;
  roster_positions: any;
  status: string | null;
  isConnected: boolean;
}

interface LeagueSettings {
  league_type: string;
  lineup_type: string;
}

const LeaguesSection = () => {
  const { user } = useAuth();
  const [sleeperUsername, setSleeperUsername] = useState<string | null>(null);
  const [sleeperUserId, setSleeperUserId] = useState<string | null>(null);
  const [connectedLeagues, setConnectedLeagues] = useState<ConnectedLeague[]>([]);
  const [discoveredLeagues, setDiscoveredLeagues] = useState<DiscoveredLeague[]>([]);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState<Set<string>>(new Set());
  const [leagueSettings, setLeagueSettings] = useState<Record<string, LeagueSettings>>({});
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(false);

  const fetchUserData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [userResult, leaguesResult] = await Promise.all([
        supabase.from('users').select('sleeper_username, sleeper_user_id').eq('user_id', user.id).maybeSingle(),
        supabase
          .from('user_leagues')
          .select('league_id, is_active, leagues(name, season_year)')
          .eq('user_id', user.id)
          .eq('is_active', true),
      ]);

      if (userResult.data?.sleeper_user_id) {
        setSleeperUserId(userResult.data.sleeper_user_id);
      }
      if (userResult.data?.sleeper_username) {
        setSleeperUsername(userResult.data.sleeper_username);
      }

      if (leaguesResult.data) {
        const mapped: ConnectedLeague[] = leaguesResult.data.map((ul: any) => ({
          league_id: ul.league_id,
          league_name: ul.leagues?.name || `League ${ul.league_id}`,
          season_year: ul.leagues?.season_year || new Date().getFullYear(),
          is_active: ul.is_active,
        }));
        setConnectedLeagues(mapped);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleDiscoverLeagues = async () => {
    const lookupIdentifier = sleeperUserId || sleeperUsername;
    if (!lookupIdentifier) {
      toast.error('No Sleeper username found. Please connect your Sleeper account first.');
      return;
    }

    setDiscovering(true);
    setShowDiscovery(true);
    setSelectedLeagueIds(new Set());
    setLeagueSettings({});

    try {
      const { data, error } = await supabase.functions.invoke('sleeper-api', {
        body: { username: lookupIdentifier, fetchPreviousYears: true },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const leagues: any[] = data?.leagues || [];
      const connectedIds = new Set(connectedLeagues.map((l) => l.league_id));

      const supersededIds = new Set(
        leagues.map((l: any) => l.previous_league_id).filter(Boolean)
      );
      const dedupedLeagues = leagues.filter((l: any) => !supersededIds.has(l.league_id));

      const discovered: DiscoveredLeague[] = dedupedLeagues.map((l: any) => ({
        league_id: l.league_id,
        name: l.name,
        season: l.season,
        total_rosters: l.total_rosters,
        avatar: l.avatar,
        settings: l.settings,
        scoring_settings: l.scoring_settings,
        roster_positions: l.roster_positions,
        status: l.status,
        isConnected: connectedIds.has(l.league_id),
      }));

      discovered.sort((a, b) => {
        if (a.isConnected !== b.isConnected) return a.isConnected ? 1 : -1;
        return a.name.localeCompare(b.name);
      });

      setDiscoveredLeagues(discovered);

      const newCount = discovered.filter((l) => !l.isConnected).length;
      if (newCount === 0) {
        toast.info('All your Sleeper leagues are already connected!');
      } else {
        toast.success(`Found ${newCount} new league${newCount > 1 ? 's' : ''} to connect.`);
      }
    } catch (err: any) {
      console.error('Error discovering leagues:', err);
      toast.error(err.message || 'Failed to discover leagues');
    } finally {
      setDiscovering(false);
    }
  };

  const unconnectedLeagues = discoveredLeagues.filter((l) => !l.isConnected);

  const toggleLeague = (leagueId: string) => {
    setSelectedLeagueIds((prev) => {
      const next = new Set(prev);
      if (next.has(leagueId)) next.delete(leagueId);
      else next.add(leagueId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeagueIds.size === unconnectedLeagues.length) {
      setSelectedLeagueIds(new Set());
    } else {
      setSelectedLeagueIds(new Set(unconnectedLeagues.map((l) => l.league_id)));
    }
  };

  const handleSettingsChange = (leagueId: string, field: 'league_type' | 'lineup_type', value: string) => {
    setLeagueSettings(prev => ({
      ...prev,
      [leagueId]: {
        ...prev[leagueId],
        [field]: value,
      },
    }));
  };

  // Check all selected leagues have both settings
  const allSettingsComplete = selectedLeagueIds.size > 0 && Array.from(selectedLeagueIds).every(id => {
    const s = leagueSettings[id];
    return s?.league_type && s?.lineup_type;
  });

  const handleConnectSelected = async () => {
    if (selectedLeagueIds.size === 0) return;

    if (!allSettingsComplete) {
      toast.error('Please select League Type and Lineup Type for all selected leagues');
      return;
    }

    setConnecting(true);

    try {
      const selectedIds = Array.from(selectedLeagueIds);

      for (const leagueId of selectedIds) {
        const league = discoveredLeagues.find((l) => l.league_id === leagueId);
        if (!league) continue;

        const seasonYear = Number(league.season) || new Date().getFullYear();
        const settings = leagueSettings[leagueId];

        await supabase.from('leagues').upsert(
          {
            id: leagueId,
            name: league.name,
            season_year: seasonYear,
            total_rosters: league.total_rosters,
            avatar: league.avatar,
            settings: league.settings,
            scoring_settings: league.scoring_settings,
            roster_positions: league.roster_positions,
            status: league.status,
            platform: 'sleeper' as const,
          },
          { onConflict: 'id' }
        );

        const { data: existing } = await supabase
          .from('user_leagues')
          .select('id')
          .eq('user_id', user!.id)
          .eq('league_id', leagueId)
          .maybeSingle();

        if (!existing) {
          await supabase.from('user_leagues').insert({
            user_id: user!.id,
            league_id: leagueId,
            is_active: true,
            platform: 'sleeper' as const,
            connected_at: new Date().toISOString(),
            league_type: settings?.league_type || null,
            lineup_type: settings?.lineup_type || null,
          });
        }

        supabase.functions.invoke('pull-sleeper-data', {
          body: { league_id: leagueId },
        }).catch((err) => console.error('Background pull error:', err));
      }

      toast.success(`Connected ${selectedIds.length} league${selectedIds.length > 1 ? 's' : ''}!`);
      setShowDiscovery(false);
      setDiscoveredLeagues([]);
      setSelectedLeagueIds(new Set());
      setLeagueSettings({});
      await fetchUserData();
    } catch (err: any) {
      console.error('Error connecting leagues:', err);
      toast.error(err.message || 'Failed to connect leagues');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">

      {/* Connected Leagues */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Connected Leagues</CardTitle>
            <CardDescription>
              {connectedLeagues.length} league{connectedLeagues.length !== 1 ? 's' : ''} connected
            </CardDescription>
          </div>
          {(sleeperUserId || sleeperUsername) && (
            <Button
              onClick={handleDiscoverLeagues}
              disabled={discovering}
              size="sm"
            >
              {discovering ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Connect More Leagues
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {connectedLeagues.length === 0 ? (
            <p className="text-sm text-muted-foreground">No leagues connected yet.</p>
          ) : (
            <div className="space-y-2">
              {connectedLeagues.map((league) => (
                <div
                  key={league.league_id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div>
                    <span className="font-medium text-sm">{league.league_name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({league.season_year})
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-green-600">
                    <Check className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Discovery Results */}
      {showDiscovery && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Discover Leagues</CardTitle>
            <CardDescription>
              {discovering
                ? 'Searching for your Sleeper leagues...'
                : `Found ${discoveredLeagues.length} total league${discoveredLeagues.length !== 1 ? 's' : ''}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {discovering ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Searching Sleeper...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select All */}
                {unconnectedLeagues.length > 1 && (
                  <div className="flex items-center space-x-2 pb-2 border-b">
                    <Checkbox
                      id="select-all"
                      checked={selectedLeagueIds.size === unconnectedLeagues.length}
                      onCheckedChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                      Select All ({unconnectedLeagues.length})
                    </label>
                  </div>
                )}

                {/* League list */}
                <div className="space-y-2">
                  {discoveredLeagues.map((league) => (
                    <div
                      key={league.league_id}
                      className={`flex flex-col gap-2 p-3 rounded-lg border ${
                        league.isConnected ? 'opacity-60 bg-muted/50' : 'bg-card'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {!league.isConnected && (
                            <Checkbox
                              id={`league-${league.league_id}`}
                              checked={selectedLeagueIds.has(league.league_id)}
                              onCheckedChange={() => toggleLeague(league.league_id)}
                            />
                          )}
                          <div>
                            <label
                              htmlFor={`league-${league.league_id}`}
                              className="font-medium text-sm cursor-pointer"
                            >
                              {league.name}
                            </label>
                            <div className="text-xs text-muted-foreground">
                              {league.total_rosters}-team &middot; {league.season} season
                            </div>
                          </div>
                        </div>
                        {league.isConnected && (
                          <Badge variant="secondary" className="text-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        )}
                      </div>

                      {/* League Type & Lineup Type selectors for unconnected leagues */}
                      {!league.isConnected && (
                        <div className="flex items-center gap-3 ml-9">
                          <Select
                            value={leagueSettings[league.league_id]?.league_type || ''}
                            onValueChange={(val) => handleSettingsChange(league.league_id, 'league_type', val)}
                          >
                            <SelectTrigger className="h-8 text-sm w-[130px]">
                              <SelectValue placeholder="League Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="dynasty">Dynasty</SelectItem>
                              <SelectItem value="redraft">Redraft</SelectItem>
                              <SelectItem value="keeper">Keeper</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select
                            value={leagueSettings[league.league_id]?.lineup_type || ''}
                            onValueChange={(val) => handleSettingsChange(league.league_id, 'lineup_type', val)}
                          >
                            <SelectTrigger className="h-8 text-sm w-[130px]">
                              <SelectValue placeholder="Lineup Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="classic">Classic</SelectItem>
                              <SelectItem value="bestball">Best Ball</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Connect button */}
                {unconnectedLeagues.length > 0 && (
                  <Button
                    onClick={handleConnectSelected}
                    disabled={!allSettingsComplete || connecting}
                    className="w-full"
                  >
                    {connecting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Connect {selectedLeagueIds.size > 0 ? `${selectedLeagueIds.size} ` : ''}
                    Selected League{selectedLeagueIds.size !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeaguesSection;
